import { useEffect, useState, useRef } from 'react';
import './AdminPage.css';

function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [questions, setQuestions] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing questionnaire data after authentication
  useEffect(() => {
    if (!authenticated) return;
    fetch('/api/admin/questionnaire-data')
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (data && data.questions) {
          setQuestions(data.questions);
          setHasUploadedFile(true);
          setStatus(`${data.count} questions loaded from saved data`);
        }
      })
      .catch(() => {});
  }, [authenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');

    fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.authenticated) {
          setAuthenticated(true);
        } else {
          setLoginError(data.error || 'Invalid password');
        }
      })
      .catch(() => setLoginError('Failed to connect to backend'))
      .finally(() => setLoggingIn(false));
  };

  const handleUpload = (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setStatus('');

    const formData = new FormData();
    formData.append('file', file);

    fetch('/api/admin/upload-questionnaire', {
      method: 'POST',
      body: formData,
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setError(data.error || 'Upload failed');
        } else {
          setStatus(data.message);
          setQuestions(data.questions);
          setHasUploadedFile(true);
        }
      })
      .catch(() => setError('Failed to connect to backend'))
      .finally(() => setUploading(false));
  };

  return (
    <div className="page">
      <div className="container">
        {!authenticated ? (
          <div className="login-wrapper">
            <div className="card login-card">
              <h2>Admin Sign In</h2>
              <p>Enter the admin password to continue.</p>
              <form onSubmit={handleLogin} className="login-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="admin-password">Password</label>
                  <input
                    id="admin-password"
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loggingIn}>
                  {loggingIn ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
              {loginError && <p className="login-error">{loginError}</p>}
            </div>
          </div>
        ) : (
          <>
            <h1>Admin</h1>

        <div className="card upload-card">
          <h3>Upload Questionnaire</h3>
          <p>Upload a questionnaire spreadsheet in <strong>.xlsx</strong> or <strong>.csv</strong> format.</p>

          <form onSubmit={handleUpload} className="upload-form">
            <div className="form-group">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                className="form-input"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>

          {error && <p className="upload-error">{error}</p>}
          {status && <p className="upload-status">{status}</p>}

          <div className="download-row">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!hasUploadedFile}
              onClick={() => {
                fetch('/api/admin/download-questionnaire')
                  .then((res) => {
                    const disposition = res.headers.get('Content-Disposition') || '';
                    const match = disposition.match(/filename="?([^"]+)"?/);
                    const filename = match ? match[1] : 'questionnaire.xlsx';
                    return res.blob().then((blob) => ({ blob, filename }));
                  })
                  .then(({ blob, filename }) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
            >
              Download Latest Spreadsheet
            </button>
          </div>
        </div>

        <div className="card field-reference">
          <h3>Spreadsheet Field Reference</h3>
          <p>The uploaded spreadsheet must contain columns with the following headers. These are mapped to JSON fields when parsed.</p>
          <div className="questions-table-wrap">
            <table className="questions-table">
              <thead>
                <tr>
                  <th>Spreadsheet Column</th>
                  <th>JSON Field</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>#</td><td>number</td><td>Supports sub-parts (e.g. 3a, 3b)</td></tr>
                <tr><td>Question</td><td>question</td><td></td></tr>
                <tr><td>Answer Options</td><td>answer_options</td><td>Newline-separated values parsed into an array</td></tr>
                <tr><td>Answers Mapping to No</td><td>no_answers</td><td>Newline-separated; answers that trigger a scoring penalty</td></tr>
                <tr><td>No Answer Score Estimate</td><td>score_estimate</td><td>Optional SPRS score estimate</td></tr>
                <tr><td>Level of Certainty</td><td>certainty_options</td><td>Newline-separated values parsed into an array</td></tr>
                <tr><td>Add Weeks</td><td>add_weeks</td><td>Weeks added when answer maps to "No"</td></tr>
                <tr><td>Control Reference</td><td>control_reference</td><td>Newline-separated CMMC control identifiers</td></tr>
                <tr><td>Lead In Info Text</td><td>lead_in_info_text</td><td>Optional introductory/hook text for the question</td></tr>
                <tr><td>Info Text</td><td>info_text</td><td>Optional detailed informational text</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {questions && questions.length > 0 && (
          <div className="card questions-preview">
            <h3>Parsed Questions ({questions.length})</h3>
            <div className="questions-table-wrap">
              <table className="questions-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Question</th>
                    <th>Add Weeks</th>
                    <th>Control Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q, i) => (
                    <tr key={i}>
                      <td>{q.number}</td>
                      <td>{q.question}</td>
                      <td>{q.add_weeks}</td>
                      <td>{q.control_reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
